/**
 * AudioUploadNode (Canvas V2)
 *
 * Upload a local audio file. Supports file picker and drag-and-drop.
 * 50MB limit, backend API upload, waveform visualization (native AudioContext),
 * play/pause toggle, mm:ss / mm:ss time display.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageUploadNode's clean,
 * media-focused card style with boxShadow for state indication.
 * No BaseNode header bar — just the audio content.
 *
 * 5 visual states:
 *   1. Empty — clickable label, microphone icon + "Upload Audio"
 *   2. Uploading — waveform area + overlay spinner
 *   3. Error — error icon + message + retry hint
 *   4. Success — waveform + playback controls always visible
 *   5. Hover — overlay buttons (download, replace)
 *
 * Output: out_audio (audio)
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { NodeProps, Position } from 'reactflow'
import { useNodeActions, FloatingNodeLabel, NODE_TYPE_ICON, UploadMediaNodeToolbar, generatedAudioPath, getPickedIndex, resolveNodeErrorMessage } from '../shared'
import type { NodeDataRecord } from '../../nodeRegistry'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useFileUpload } from '../shared/useFileUpload'
import { AudioWaveformVisualization } from '../shared/AudioWaveformVisualization'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useTranslation } from 'react-i18next'

import { dataStatus,dataStr,dataStrOpt } from '../../../../hooks/useYCanvas'
import { type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

const NODE_WIDTH = 315
type AudioUploadNodeData = ReactFlowCanvasNodeDataByType['audio']

const AudioUploadNode = memo(({ id, data, selected, dragging }: NodeProps<AudioUploadNodeData>) => {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const progressMessage = dataStrOpt(data, 'progressMessage')

  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'audio', data)
  const projectId = useProjectId()

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: NodeDataRecord<'audio'>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = updates.in_file_name.replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      // Write schema-declared fields via typed node handle
      if (merged.in_file_name !== undefined && typeof merged.in_file_name === 'string') {
        node?.writeInFileName(merged.in_file_name)
      }
      // Write remaining non-schema fields via canvas
      canvas?.updateNodeData({ id, type: 'audio' }, merged)
    },
    [id, canvas, node]
  )

  const out_audio = node?.get('out_audio')
  const in_file_name = node?.get('in_file_name')

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    nodeId: id,
    acceptMime: 'audio',
    updateNodeData,
    persistedValue: out_audio,
    initialPreviewUrl: dataStrOpt(data, '_initialPreviewUrl'),
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFile = data.initialFile instanceof File ? data.initialFile : undefined
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(initialFile)
    }
  }, [initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      setTimeout(() => {
        canvas?.updateNodeData({ id, type: 'audio' }, { initialFile: undefined })
      }, 100)
    }
  }, [initialFile, id, canvas])

  const { handleDownload } = useNodeActions({ nodeId: id })

  const outAudioHistory = out_audio?.history ?? []
  const outAudioPath = generatedAudioPath(outAudioHistory[getPickedIndex(out_audio)])
  const persistedUrl = outAudioPath ? api.getAssetUrl(projectId, outAudioPath) : undefined
  const displayUrl = previewUrl || persistedUrl

  const onDownload = useCallback(() => {
    if (outAudioPath) {
      handleDownload(outAudioPath, in_file_name || 'uploaded-audio.mp3')
    }
  }, [outAudioPath, in_file_name, handleDownload])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(outAudioPath))

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData({ id, type: 'audio' }, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  const hasError = isError && !!error
  const hasExternalProgress = !!progressMessage && !outAudioPath && !error && !isError
  const isRunning = status === 'running' || hasExternalProgress
  const isBusy = isUploading || isRunning
  const busyMessage = isRunning
    ? progressMessage || t('audioUploadNode.extractingAudio')
    : t('audioUploadNode.uploadingAudio')
  const extractionMetaLoading: MetaLoadingDescriptor | undefined = isRunning
    ? {
        message: busyMessage,
        nodeType: 'audio',
        loadingState: status === 'running' ? 'running' : 'processing',
      }
    : undefined

  return (
    <div className="relative">
      <UploadMediaNodeToolbar
        nodeId={id}
        selected={selected}
        dragging={dragging}
        originalName={derivedLabel || dataStr(data, 'label') || t('audioUploadNode.untitled')}
      />

      {/* Floating editable label above the card */}
      <FloatingNodeLabel
        name={derivedLabel || dataStr(data, 'label') || ''}
        fallback={t('audioUploadNode.untitled')}
        typeIcon={NODE_TYPE_ICON.audio}
        onRename={handleRename}
      />

      {/* Card wrapper — matches ImageUploadNode's boxShadow pattern */}
      <div
        className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden transition-all duration-150 ${
          isRunning ? 'border-2 animate-border-glow' : ''
        }`}
        style={{
          width: nodePresentationWidth(NODE_WIDTH),
          boxShadow: selected
            ? '0 0 0 2px rgba(255,255,255,0.7)'
            : hasError
              ? '0 0 0 1px rgba(239,68,68,0.5)'
              : isRunning
                ? '0 0 0 1px rgba(6,182,212,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
        }}
        {...(!isBusy ? dragHandlers : {})}
      >
        {/* Hidden file input */}
        <input
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
          className="hidden"
          id={inputId}
          disabled={isBusy}
        />

        {/* Main content area — 5 visual states */}
        {isUploading && displayUrl ? (
          /* -- State 2: Uploading or extracting — waveform area + overlay spinner -- */
          <div className="relative bg-canvas p-3">
            <AudioWaveformVisualization
              url={displayUrl}
              height={80}
            />

            {/* Overlay spinner */}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center rounded-lg">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-xxs text-white/80 mt-2">{busyMessage}</span>
            </div>
          </div>
        ) : hasError ? (
          /* -- State 3: Error — icon + message + retry -- */
          <div
            className="relative w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
            style={{ aspectRatio: '16/9' }}
            onClick={retry}
          >
            <svg
              className="w-8 h-8 text-foreground/25"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <span className="text-xs font-medium text-foreground/50 mt-1.5">{t('audioUploadNode.clickToRetry')}</span>
            {/* Status overlay — the shared, neutral treatment (post 1634117708345526). */}
            <NodeStatusMessage
              message={resolveNodeErrorMessage(error, t) || t('audioUploadNode.failedToUpload')}
              testId="upload-card-error"
            />
          </div>
        ) : extractionMetaLoading ? (
          <div
            className="relative w-full bg-canvas"
            data-testid="audio-upload-running-state"
            style={displayUrl ? { height: 104 } : { aspectRatio: '16/9' }}
          >
            <MetaLoadingSurface
              {...extractionMetaLoading}
              projectId={projectId}
              nodeId={id}
              presentation="full"
            />
          </div>
        ) : displayUrl ? (
          /* -- State 4 + 5: Success — waveform + playback always visible, hover shows overlay buttons -- */
          <div className="relative group bg-canvas p-3">
            <AudioWaveformVisualization
              url={displayUrl}
              height={80}
            />

            {/* -- State 5: Hover overlay buttons — download, replace -- */}
            <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Download */}
              <button
                onClick={(e) => { e.stopPropagation(); onDownload() }}
                className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                title={t('audioUploadNode.download')}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              {/* Replace — label triggers file picker */}
              <label
                htmlFor={inputId}
                className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                title={t('audioUploadNode.replace')}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </label>
            </div>
          </div>
        ) : isUploading ? (
          /* -- State 2: Uploading without a local preview yet -- */
          <div
            className="relative w-full bg-canvas flex flex-col items-center justify-center px-6"
            data-testid="audio-upload-running-state"
            style={{ aspectRatio: '16/9' }}
          >
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-medium text-foreground/70 mt-2 text-center">{busyMessage}</span>
          </div>
        ) : (
          /* -- State 1: Empty — clickable upload area -- */
          <label
            htmlFor={inputId}
            className={`w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
              isDragOver
                ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                : ''
            }`}
            style={{ aspectRatio: '16/9' }}
          >
            {isDragOver ? (
              <span className="text-xs font-medium text-primary">{t('audioUploadNode.dropToUpload')}</span>
            ) : (
              <>
                <svg
                  className="w-8 h-8 text-foreground/15"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
                <span className="text-xxs text-foreground/30 mt-1">{t('audioUploadNode.uploadAudio')}</span>
              </>
            )}
          </label>
        )}

        {/* Multi-file drop warning */}
        {multiFileWarning && (
          <div className="px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
            <span className="text-xxs text-amber-400">{multiFileWarning}</span>
          </div>
        )}
      </div>

      {/* Character voice ownership handle — visually hidden, kept in DOM for edges. */}
      <TypedHandle
        nodeId={id}
        type="target"
        position={Position.Left}
        id="in_voice_profile"
        portType="VoiceProfileDict"
        style={{
          top: '50%',
          width: 0,
          height: 0,
          border: 'none',
          background: 'transparent',
          minWidth: 0,
          minHeight: 0,
        }}
      />

      {/* Output handle: out_audio — visually hidden, kept in DOM for edges */}
      <TypedHandle
        nodeId={id}
        type="source"
        position={Position.Right}
        id="out_audio"
        portType="audio"
        style={{ top: '50%', width: 0, height: 0, border: 'none', background: 'transparent', minWidth: 0, minHeight: 0 }}
      />
    </div>
  )
})

AudioUploadNode.displayName = 'AudioUploadNode'
export default AudioUploadNode
import { useNodePresentationWidth } from '../../NodePresentationContext'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
